import React, { useState } from "react";
import { Grid, Box, Typography, Dialog, DialogContent, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import { MAX_FILE_SIZE } from "config/constants";

// Styled component for the container
const Container = styled(Box)(({ theme }) => ({
  backgroundColor: "#fcfcfc",
  borderRadius: "20px",
  padding: theme.spacing(4),
  textAlign: "center",
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed",
  borderColor: "#7DD3FC",
  borderRadius: "20px",
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

const FileInput = styled("input")({
  display: "none",
});

const PreviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  marginTop: theme.spacing(2),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const PreviewItem = styled(Box)(({ theme }) => ({
  position: "relative",
}));

const PreviewImage = styled("img")({
  width: 100,
  height: 100,
  borderColor: "green",
  borderWidth: 1,
  borderStyle: "solid",
  objectFit: "cover",
  borderRadius: 8,
  cursor: "pointer",
  "&:hover": {
    transform: "scale(1.1)",
    transition: "transform 0.2s",
  },
});

const FullScreenImage = styled("img")({
  width: "100%",
  height: "auto",
});

const ImagesUpload = ({ handleImageUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const validImagesMimeTypes = [
    'image/gif',    
    'image/jpeg',    
    'image/png',
  ]

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prevFiles) => {
      const imgs = [
        ...prevFiles,
        ...files
          .map((image) => {
            
            if (!validImagesMimeTypes.includes(image.type)) {
              // toast.error(`${image.name} is not a valid image type`);
              return null;
            }

            if (image.size > MAX_FILE_SIZE) {
              // toastr.error(`${image.name} exceeds the maximum file size of 20 MB`);
              return null;
            }
      
            return {
              blob: URL.createObjectURL(image),
              file: image,
            };
          })
          .filter((image) => image !== null), // Filter out null values
      ];
      handleImageUpload(imgs);
      return imgs;
    });

  };

  const handleClickOpen = (image) => {
    setSelectedImage(image);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImage(null);
  };

  const handleRemoveImage = (image) => {
    setSelectedFiles((prevFiles) => {
      const imgs = prevFiles.filter((file) => file.blob !== image);
      handleImageUpload(imgs);
      return imgs;
    });
  };

  return (
    <Grid item xs={12} md={12} lg={12}>
      <Container>
        <Typography variant="h6" component="div" gutterBottom>
          Upload Image
        </Typography>
        <Typography variant="body2" color="textSecondary">
          PNG, JPG and GIF are accepted
        </Typography>
        <label htmlFor="file-upload">
          <UploadBox>
            <CloudUploadIcon color="info" fontSize="large" />
            <Typography variant="body1" color="textPrimary" sx={{ mt: 2 }}>
              Browse to choose the file here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Maximum file size should not be more than <strong>20MB</strong>
            </Typography>
          </UploadBox>
        </label>
        <FileInput id="file-upload" type="file" multiple onChange={handleFileChange} />
        <PreviewContainer>
          {selectedFiles.map((file, index) => (
            <PreviewItem key={index}>
              <PreviewImage
                src={file.blob}
                alt={`upload-${index}`}
                onClick={() => handleClickOpen(file.blob)}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                }}
                onClick={() => handleRemoveImage(file.blob)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </PreviewItem>
          ))}
        </PreviewContainer>
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
          <DialogContent>
            {selectedImage && (
              <FullScreenImage src={selectedImage} alt="Selected" onClick={handleClose} />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Grid>
  );
};

export default ImagesUpload;
